import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import {
	Box,
	Button,
	ClickAwayListener,
	Divider,
	IconButton,
	Paper,
	Popper,
	Stack,
	TextField,
	type TextFieldProps,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import type { DateOnly, DateOnlyFormat } from "./dateOnly";
import {
	DATE_ONLY_FORMAT,
	addDays,
	addMonths,
	formatDateOnly,
	isDateAfter,
	isDateBefore,
	parseTypedDate,
	getStartOfMonth,
	getToday,
} from "./dateOnly";
import { RangeMonth } from "./RangeMonth";

export interface DateRange {
	start: DateOnly | null;
	end: DateOnly | null;
}

const EMPTY_RANGE: DateRange = { start: null, end: null };

const isOutOfBounds = ({
	date,
	minDate,
	maxDate,
}: {
	date: DateOnly;
	minDate?: DateOnly;
	maxDate?: DateOnly;
}) =>
	(minDate !== undefined && isDateBefore(date, minDate)) ||
	(maxDate !== undefined && isDateAfter(date, maxDate));

const isDayDisabled = ({
	date,
	minDate,
	maxDate,
	shouldDisableDate,
}: {
	date: DateOnly;
	minDate?: DateOnly;
	maxDate?: DateOnly;
	shouldDisableDate?: (date: DateOnly) => boolean;
}) =>
	isOutOfBounds({ date, minDate, maxDate }) ||
	(shouldDisableDate?.(date) ?? false);

export interface DateRangePickerProps {
	/** The selected range. Fully controlled: the picker holds no value of its own. */
	value: DateRange;
	/**
	 * Called on every change, including the intermediate `{ start, end: null }` the first
	 * click produces. A consumer that only cares about complete ranges should check `end`.
	 */
	onChange: (value: DateRange) => void;
	/**
	 * How the two fields render a date. Only affects display: typing is parsed leniently
	 * whatever this says, so `6/1`, `060126` and `06/01/2026` are all accepted.
	 * @default DATE_ONLY_FORMAT.LONG
	 */
	format?: DateOnlyFormat;
	/** @default { start: "Start date", end: "End date" } */
	labels?: { start: string; end: string };
	/** Earliest selectable day, inclusive. */
	minDate?: DateOnly;
	/** Latest selectable day, inclusive. */
	maxDate?: DateOnly;
	/**
	 * Days the range may not start or end on. Called for every rendered cell, so keep it
	 * cheap -- capture a lookup structure rather than searching a list per call.
	 */
	shouldDisableDate?: (date: DateOnly) => boolean;
	/**
	 * Whether a range may *contain* a disabled day.
	 *
	 * Leave it off when a disabled day marks a span that is already taken (a period already
	 * billed, a booked room): jumping over one is as invalid as landing on it, and the
	 * picker greys out everything past the first blocked day while the end is being chosen.
	 *
	 * Turn it on when a disabled day only means "not a valid endpoint" -- a holiday, a day
	 * with no readings -- and a range is free to span it.
	 * @default false
	 */
	allowDisabledInside?: boolean;
	/**
	 * Months side by side in the popover. Two makes a range that crosses a month boundary
	 * selectable without paging.
	 * @default 2
	 */
	monthsShown?: 1 | 2;
	/** Blocks both fields and the calendar button. */
	disabled?: boolean;
	/** @default "small" */
	size?: TextFieldProps["size"];
	/** Stretches the two fields to fill their container. @default false */
	fullWidth?: boolean;
	/** Shows the button that empties the range from inside the popover. @default false */
	clearable?: boolean;
	/** Passed to both fields -- `variant`, `sx`, `InputProps` and the rest. */
	slotProps?: { textField?: Partial<TextFieldProps> };
}

/**
 * A two-field date range picker with a calendar popover, built on MUI core alone -- the
 * range picker MUI ships is part of the paid X Pro package.
 *
 * Values are calendar days as `"YYYY-MM-DD"` strings, never `Date`: a range of days has no
 * time zone, and keeping it out of `Date` is what stops a period from shifting by one day
 * between the server, the field and the calendar.
 *
 * @example
 * const [range, setRange] = useState<DateRange>({ start: null, end: null });
 *
 * <DateRangePicker
 *   value={range}
 *   onChange={setRange}
 *   shouldDisableDate={(day) => takenDays.has(day)}
 * />
 */
export const DateRangePicker = ({
	value,
	onChange,
	format = DATE_ONLY_FORMAT.LONG,
	labels = { start: "Start date", end: "End date" },
	minDate,
	maxDate,
	shouldDisableDate,
	allowDisabledInside = false,
	monthsShown = 2,
	disabled = false,
	size = "small",
	fullWidth = false,
	clearable = false,
	slotProps,
}: DateRangePickerProps) => {
	const anchorRef = useRef<HTMLDivElement>(null);

	const [isOpen, setIsOpen] = useState(false);
	const [isPickingEnd, setIsPickingEnd] = useState(false);
	const [hovered, setHovered] = useState<DateOnly | null>(null);
	const [firstMonth, setFirstMonth] = useState(() =>
		getStartOfMonth(value.start ?? getToday()),
	);
	const [startText, setStartText] = useState("");
	const [endText, setEndText] = useState("");

	const isSelectable = (date: DateOnly) => {
		if (isDayDisabled({ date, minDate, maxDate, shouldDisableDate }))
			return false;
		if (allowDisabledInside || !isPickingEnd || value.start === null)
			return true;

		const [from, to] = isDateBefore(date, value.start)
			? [date, value.start]
			: [value.start, date];
		for (let day = from; !isDateAfter(day, to); day = addDays(day, 1)) {
			if (isDayDisabled({ date: day, minDate, maxDate, shouldDisableDate }))
				return false;
		}
		return true;
	};

	const currentRange =
		value.start !== null && value.end !== null
			? { from: value.start, to: value.end }
			: null;

	const previewRange = (() => {
		if (hovered === null) return null;

		if (value.start !== null && value.end === null) {
			return isDateBefore(hovered, value.start)
				? { from: hovered, to: addDays(value.start, -1) }
				: { from: addDays(value.start, 1), to: hovered };
		}

		if (currentRange === null) return null;
		if (isDateAfter(hovered, currentRange.to))
			return { from: addDays(currentRange.to, 1), to: hovered };
		if (isDateBefore(hovered, currentRange.from))
			return { from: hovered, to: addDays(currentRange.from, -1) };
		return null;
	})();

	const open = () => {
		if (disabled || isOpen) return;
		setFirstMonth(getStartOfMonth(value.start ?? getToday()));
		setIsPickingEnd(false);
		setHovered(null);
		setIsOpen(true);
	};

	const handleClickAway = (event: globalThis.MouseEvent | TouchEvent) => {
		if (anchorRef.current?.contains(event.target as Node)) return;
		setIsOpen(false);
	};

	const selectDate = (date: DateOnly) => {
		if (value.start !== null && value.end !== null) {
			if (isDateAfter(date, value.end)) {
				onChange({ start: value.start, end: date });
				setHovered(null);
				setIsOpen(false);
				return;
			}
			if (isDateBefore(date, value.start)) {
				onChange({ start: date, end: value.end });
				setHovered(null);
				setIsOpen(false);
				return;
			}
		}

		if (!isPickingEnd || value.start === null) {
			onChange({ start: date, end: null });
			setIsPickingEnd(true);
			return;
		}

		const range = isDateBefore(date, value.start)
			? { start: date, end: value.start }
			: { start: value.start, end: date };
		onChange(range);
		setIsPickingEnd(false);
		setHovered(null);
		setIsOpen(false);
	};

	const commitText = (which: "start" | "end", text: string) => {
		const parsed = parseTypedDate(text);
		if (text.trim() === "") {
			onChange(
				which === "start" ? { ...value, start: null } : { ...value, end: null },
			);
			return;
		}
		if (
			parsed === null ||
			isDayDisabled({ date: parsed, minDate, maxDate, shouldDisableDate })
		) {
			setStartText(formatDateOnly(value.start, format));
			setEndText(formatDateOnly(value.end, format));
			return;
		}

		const next =
			which === "start"
				? { ...value, start: parsed }
				: { ...value, end: parsed };

		onChange(
			next.start !== null &&
				next.end !== null &&
				isDateBefore(next.end, next.start)
				? { start: next.end, end: next.start }
				: next,
		);
	};

	useEffect(() => {
		setStartText(formatDateOnly(value.start, format));
		setEndText(formatDateOnly(value.end, format));
	}, [value.start, value.end, format]);

	useEffect(() => {
		if (!isOpen) return;
		const onKeyDown = (event: globalThis.KeyboardEvent) => {
			if (event.key === "Escape") setIsOpen(false);
		};
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [isOpen]);

	const noAutofillProps = {
		autoComplete: "off",
		spellCheck: false,
		"data-lpignore": "true",
		"data-form-type": "other",
	} as const;

	const fieldProps: Partial<TextFieldProps> = {
		size,
		fullWidth,
		disabled,
		...slotProps?.textField,
	};

	return (
		<>
			<Stack
				ref={anchorRef}
				direction="row"
				spacing={1}
				alignItems="center"
				sx={{ width: fullWidth ? "100%" : undefined }}
			>
				<TextField
					{...fieldProps}
					label={labels.start}
					value={startText}
					onChange={(event) => setStartText(event.target.value)}
					onBlur={(event) => commitText("start", event.target.value)}
					onKeyDown={(event) => {
						if (event.key === "Enter") commitText("start", startText);
					}}
					inputProps={{
						...noAutofillProps,
						...fieldProps.inputProps,
						name: "date-range-start",
						onClick: () => open(),
					}}
				/>
				<Box sx={{ color: "text.secondary", px: 0.5 }}>-</Box>
				<TextField
					{...fieldProps}
					label={labels.end}
					value={endText}
					onChange={(event) => setEndText(event.target.value)}
					onBlur={(event) => commitText("end", event.target.value)}
					onKeyDown={(event) => {
						if (event.key === "Enter") commitText("end", endText);
					}}
					inputProps={{
						...noAutofillProps,
						...fieldProps.inputProps,
						name: "date-range-end",
						onClick: () => open(),
					}}
				/>
			</Stack>

			{/* A Popper and not a Popover: the latter is a modal, and its backdrop would swallow
          the click meant for the fields -- closing the calendar just as it reopens. */}
			<Popper
				open={isOpen}
				anchorEl={anchorRef.current}
				placement="bottom-start"
				sx={{ zIndex: (theme) => theme.zIndex.modal }}
			>
				<ClickAwayListener onClickAway={handleClickAway}>
					<Paper
						elevation={8}
						sx={{ borderRadius: 2, mt: 0.5, p: 2, position: "relative" }}
					>
						<IconButton
							size="small"
							onClick={() => setFirstMonth(addMonths(firstMonth, -1))}
							sx={{ left: 8, position: "absolute", top: 12 }}
						>
							<ChevronLeft fontSize="small" />
						</IconButton>
						<IconButton
							size="small"
							onClick={() => setFirstMonth(addMonths(firstMonth, 1))}
							sx={{ position: "absolute", right: 8, top: 12 }}
						>
							<ChevronRight fontSize="small" />
						</IconButton>
						<Stack
							direction="row"
							spacing={3}
							divider={
								monthsShown > 1 ? (
									<Divider orientation="vertical" flexItem />
								) : undefined
							}
						>
							{Array.from({ length: monthsShown }, (_, index) => (
								<RangeMonth
									key={index}
									month={addMonths(firstMonth, index)}
									currentRange={currentRange}
									previewRange={previewRange}
									edges={[value.start, value.end]}
									isDateDisabled={(date) => !isSelectable(date)}
									onSelect={selectDate}
									onHover={setHovered}
								/>
							))}
						</Stack>

						{clearable && (
							<Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}>
								<Button
									size="small"
									onClick={() => {
										onChange(EMPTY_RANGE);
										setIsPickingEnd(false);
									}}
								>
									Clear
								</Button>
							</Box>
						)}
					</Paper>
				</ClickAwayListener>
			</Popper>
		</>
	);
};
